import React from 'react';
import { motion } from 'framer-motion';

/**
 * @interface SpinnerProps
 * @description Define as propriedades para o componente Spinner.
 */
interface SpinnerProps {
  /**
   * @property {('sm' | 'md' | 'lg' | 'xl')} [size='md']
   * @description Define o tamanho visual do spinner.
   * - 'sm': Pequeno (h-5 w-5 border-2)
   * - 'md': Médio (h-8 w-8 border-4) - Padrão
   * - 'lg': Grande (h-12 w-12 border-4)
   * - 'xl': Extra grande (h-16 w-16 border-8)
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * @property {('primary' | 'secondary' | 'accent' | 'danger' | 'warning' | 'success' | 'text')} [color='primary']
   * @description Define a cor principal do spinner, utilizando a paleta de cores do Design System.
   * - 'primary': Cor primária (#d9a526)
   * - 'secondary': Cor secundária (#e8c97d)
   * - 'accent': Cor de destaque (#c38d09)
   * - 'danger': Cor de perigo (#EF4444)
   * - 'warning': Cor de aviso (#F59E0B)
   * - 'success': Cor de sucesso (#10B981)
   * - 'text': Cor de texto (#f2eee3)
   * Padrão: 'primary'.
   */
  color?: 'primary' | 'secondary' | 'accent' | 'danger' | 'warning' | 'success' | 'text';
  /**
   * @property {string} [className='']
   * @description Classes CSS adicionais para aplicar estilos personalizados ao spinner.
   */
  className?: string;
}

/**
 * @function Spinner
 * @description Componente de UI para exibir um indicador de carregamento animado.
 * Utiliza Framer Motion para uma animação suave e Tailwind CSS para estilização moderna e responsiva.
 * O spinner é um anel rotativo com uma seção de cor mais clara para um efeito visual dinâmico.
 *
 * @param {SpinnerProps} props - As propriedades para configurar o spinner.
 * @returns {JSX.Element} Um elemento JSX que representa o spinner.
 */
const Spinner: React.FC<SpinnerProps> = ({ size = 'md', color = 'primary', className = '' }) => {
  /**
   * @constant tamanhoClasses
   * @description Objeto que mapeia os tamanhos de spinner para classes Tailwind CSS
   * que controlam altura, largura e espessura da borda.
   */
  const tamanhoClasses = {
    sm: 'h-5 w-5 border-2',
    md: 'h-8 w-8 border-4',
    lg: 'h-12 w-12 border-4',
    xl: 'h-16 w-16 border-8',
  };

  /**
   * @constant corBaseClasses
   * @description Objeto que mapeia as cores para classes Tailwind CSS que definem
   * a cor principal da borda do spinner.
   */
  const corBaseClasses = {
    primary: 'border-primary',
    secondary: 'border-primary/20',
    accent: 'border-primary/20',
    danger: 'border-danger',
    warning: 'border-warning',
    success: 'border-success',
    text: 'border-text',
  };

  /**
   * @constant corParteAnimadaClasses
   * @description Objeto que mapeia as cores para classes Tailwind CSS que definem
   * a cor da parte "transparente" da borda do spinner, usando opacidade reduzida
   * para um efeito visual mais suave e moderno.
   */
  const corParteAnimadaClasses = {
    primary: 'border-t-primary/30',
    secondary: 'border-t-secondary/30',
    accent: 'border-t-accent/30',
    danger: 'border-t-danger/30',
    warning: 'border-t-warning/30',
    success: 'border-t-success/30',
    text: 'border-t-text/30',
  };

  /**
   * @constant spinnerVariants
   * @description Define as variantes de animação para Framer Motion, controlando
   * a rotação contínua do spinner.
   */
  const spinnerVariants = {
    spin: {
      rotate: 360,
      transition: {
        duration: 1,
        ease: 'linear',
        repeat: Infinity,
      },
    },
  };

  return (
    <motion.div
      className={`
        inline-block
        rounded-full
        border-solid
        ${tamanhoClasses[size]}
        ${corBaseClasses[color]}
        ${corParteAnimadaClasses[color]}
        ${className}
      `}
      variants={spinnerVariants}
      initial={{ rotate: 0 }}
      animate="spin"
      role="status"
      aria-label="Carregando..."
    >
      <span className="sr-only">Carregando...</span>
    </motion.div>
  );
};

export default Spinner;